import PlusIcon from "@/common/svgs/plus-icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import useEditProfile from "../hooks/use-edit-profile";

const baseURL = import.meta.env.VITE_SERVER_API_URL;

function EditProfile() {
	const {
		user,

		countries,
		states,

		preview,
		handleProfilePictureChange,

		form,
		onSubmit,
	} = useEditProfile();

	const initials = user?.fullName
		.split(" ")
		.map((word) => word[0]?.toUpperCase())
		.join("");

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="flex flex-col gap-12"
			>
				<div className="flex w-full flex-col gap-6">
					<p className="web-heading-h4">Edit Profile</p>

					<div className="flex flex-col-reverse gap-22 md:flex-row">
						<div className="flex w-full flex-col gap-6">
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>First name</FormLabel>
										<FormControl>
											<Input
												{...field}
												required
												placeholder="Type your first name"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Last name</FormLabel>
										<FormControl>
											<Input
												{...field}
												required
												placeholder="Type your last name"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex w-full flex-col gap-6 md:flex-row md:gap-8">
								<div className="flex-1">
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Email</FormLabel>
												<FormControl>
													<Input
														{...field}
														required
														disabled
														type={"email"}
														placeholder="Email address"
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>

								<div className="flex-1">
									<FormField
										control={form.control}
										name="mobileNumber"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Mobile Number</FormLabel>
												<FormControl>
													<Input
														{...field}
														required
														type={"tel"}
														placeholder="Enter mobile number"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							<FormField
								control={form.control}
								name="countryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Country</FormLabel>
										<Select disabled value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="Select a country" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{countries.map((country) => (
													<SelectItem
														key={country.id}
														value={country.id.toString()}
													>
														{country.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>

							<div className="flex flex-col gap-6 md:flex-row md:gap-8">
								<div className="flex-1">
									<FormField
										control={form.control}
										name="stateId"
										render={({ field }) => (
											<FormItem>
												<FormLabel>State</FormLabel>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													value={field.value}
												>
													<FormControl>
														<SelectTrigger>
															<SelectValue placeholder="Select a state" />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{states.map((state) => (
															<SelectItem
																key={state.id}
																value={state.id.toString()}
															>
																{state.name}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormItem>
										)}
									/>
								</div>

								<div className="flex-1">
									<FormField
										control={form.control}
										name="city"
										render={({ field }) => (
											<FormItem>
												<FormLabel>City</FormLabel>
												<FormControl>
													<Input {...field} required placeholder="City" />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							<div className="flex flex-col gap-6 md:flex-row md:gap-8">
								<div className="flex-1">
									<FormField
										control={form.control}
										name="streetAddress"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Street Address</FormLabel>
												<FormControl>
													<Input
														{...field}
														required
														placeholder="Street address"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="flex-1">
									<FormField
										control={form.control}
										name="zipCode"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Zip Code</FormLabel>
												<FormControl>
													<Input {...field} required placeholder="Zip code" />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>

						<FormField
							control={form.control}
							name="image"
							render={() => (
								<FormItem>
									<FormControl>
										<div className="flex flex-col gap-8">
											<Avatar
												style={{
													height: 150,
													width: 150,
												}}
											>
												{preview ? (
													<AvatarImage src={preview} />
												) : (
													<AvatarImage
														src={`${baseURL}/common/profile-picture/${user.profilePictureUrl}`}
													/>
												)}
												<AvatarFallback>{initials}</AvatarFallback>
											</Avatar>

											<div
												className="flex cursor-pointer flex-row gap-1"
												onClick={() =>
													document.getElementById("avatarInput")?.click()
												}
											>
												<PlusIcon />
												<p className="web-button text-blue-700">
													Select avatar
												</p>
											</div>

											<Input
												id="avatarInput"
												type="file"
												accept="image/*"
												className="hidden"
												onChange={handleProfilePictureChange}
											/>
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				<Button type="submit" className="md:w-60">
					Save Changes
				</Button>
			</form>
		</Form>
	);
}

export default EditProfile;
